/**
 * Removes leading numbers from a given string.
 *
 * The function checks if the string starts with one or more digits, optionally followed
 * by a space, dot, or hyphen. If such a pattern is found, it removes it from the string.
 * The remaining content of the string is returned unchanged.
 *
 * @param {string} string - The input string from which leading numbers should be removed.
 * @returns {string} The cleaned string without leading numbers.
 *
 * @example
 * removeNumbersFromStartOfTheString("01 Acceleration as a vector");
 * // Returns: "Acceleration as a vector"
 *
 * @example
 * removeNumbersFromStartOfTheString("01 Acceleration-2 as a vector");
 * // Returns: "Acceleration-2 as a vector"
 *
 * @example
 * removeNumbersFromStartOfTheString("01 Acceleration 2 as a vector");
 * // Returns: "Acceleration 2 as a vector"
 *
 * @example
 * removeNumbersFromStartOfTheString("01 Acceleration as a vector 22");
 * // Returns: "Acceleration as a vector 22"
 */

export function removeNumbersFromStartOfTheString(string: string): string {
  return string.replace(/^\d+[\s\.-]?\s*/, '')
}

/**
 * Finds the best matching correct value for a broken field value
 * @param brokenValue The incorrect field value that needs fixing
 * @param correctValues Array of possible correct values
 * @returns The best matching correct value, or the original if no good match found
 */
export function matchToClosestCorrectValue(
  brokenValue: string,
  correctValues: string[],
): string {
  if (!brokenValue || !correctValues?.length) return brokenValue

  const lowerBroken = brokenValue.toLowerCase().trim()
  const correctValuesLower = correctValues.map((v) => v.toLowerCase())

  // 1. Try exact match (case insensitive)
  const exactMatchIndex = correctValuesLower.indexOf(lowerBroken)
  if (exactMatchIndex >= 0) return correctValues[exactMatchIndex]

  // 2. Try match after removing all non-alphanumeric chars
  const cleanBroken = lowerBroken.replace(/[^a-z0-9]/g, '')
  const cleanMatchIndex = correctValuesLower.findIndex(
    (v) => v.replace(/[^a-z0-9]/g, '') === cleanBroken,
  )
  if (cleanMatchIndex >= 0) return correctValues[cleanMatchIndex]

  // 3. Try acronym match (e.g., "ial" matches "IAL")
  const acronymMatchIndex = correctValuesLower.findIndex(
    (v) =>
      v.replace(/[^a-z0-9]/g, '') === cleanBroken ||
      cleanBroken === v.replace(/[^a-z0-9]/g, ''),
  )
  if (acronymMatchIndex >= 0) return correctValues[acronymMatchIndex]

  // If no match found, return original value
  return brokenValue
}
